/**
 * *Positions Groups Excluded*: the dropdown, on the three screens that declare
 * a statistic.
 *
 * A statistic may name the Canonical Position Groups it does not rank, and
 * three screens can set it — the Stat Dictionary's add form, its row editor,
 * and the new-statistic form on both field-mapping screens. One control, for the
 * reason `FieldMapping.tsx` gives for `StatisticCell`: the last pair of
 * field-mapping controls written out twice drifted, and the screen ended up
 * reporting kept data as deleted while typechecking perfectly.
 *
 * **Native checkboxes inside a floating panel, not a bespoke multi-select
 * listbox.** `StatisticMenu` builds the second kind, and it is the right answer
 * there: 392 rows that have to be searched, with a detail panel under them. This
 * list is fourteen options at most — football's thirteen groups and `UNKNOWN` —
 * and nothing about it needs searching. What it does need is for Space to
 * toggle, Tab to walk, and a screen reader to announce "checked"; a `role`
 * attribute per row would be re-implementing all three, and the suite here
 * cannot drive a browser to find out whether the re-implementation works.
 *
 * **It is a `fixed` panel for the measured reason in `usePlacement`**, which
 * this borrows whole: the field table is wrapped in `TableScroll`, whose
 * `overflow-x-auto` makes the vertical axis clip too, so an absolutely
 * positioned panel on the last row of a long table is a sliver of one line.
 *
 * **The shut control says what is excluded, and says "No exclusions" when
 * nothing is.** A blank would read as a column nobody has filled in — on the one
 * screen every Source Profile in the sport reads. The wording and the capping
 * are `position-exclusions.ts`, which holds no React so `tests/` can assert
 * them.
 */

import { useRef, useState } from "react";

import { NO_EXCLUSIONS, summarise, toggle } from "../position-exclusions";
import { AddPositionGroupModal, type DraftSource } from "./AddPositionGroup";
import { usePlacement } from "./Combobox";
import { Button, Checkbox, cx } from "./ui";
import { CloseIcon, PlusIcon } from "./icons";

export function PositionGroupPicker({
  options,
  labels,
  value,
  onChange,
  onCommit,
  sport,
  profileExists = true,
  draft,
  compact = false,
  disabled = false,
  label = "Positions Groups Excluded",
}: {
  /**
   * The groups this row may exclude, in the sport's own order.
   *
   * Handed in rather than fetched here, and already including anything the
   * value holds that the sport no longer offers — `optionsFor` is that rule.
   * A control that fetched its own options would be one request per row on a
   * table of 488.
   */
  options: readonly string[];
  /**
   * What feeds each group, keyed by group name.
   *
   * **A group is made of its labels, so the list says so.** Having added a
   * group, there was nothing on this screen that answered "what is in it?" —
   * and a group somebody has just invented reads the same as a typo until its
   * labels are beside it. Optional because the control is useful without it
   * and one caller may not have the query resolved yet; a group with no entry
   * simply shows nothing rather than an empty parenthesis.
   */
  labels?: Readonly<Record<string, readonly string[]>>;
  value: readonly string[];
  onChange: (groups: string[]) => void;
  /**
   * Save this, now.
   *
   * **What makes an in-place cell one request instead of one per tick.** The
   * Stat Dictionary's table edits its Direction from a `<select>`, where one
   * change is one deliberate act — but a multi-select is not: excluding three
   * groups would be three writes to the column that decides which positions a
   * statistic ranks, and the third would be read by every source in the sport.
   * So the cell holds a draft, `onChange` moves it, and this is where it saves —
   * the discipline `DefaultWeightCell` already applies by committing on blur
   * rather than on a keystroke.
   *
   * **It carries the groups rather than letting the caller read its own
   * state**, which is not tidiness. Adding a group selects it and saves in one
   * act, so the two would happen in one tick — and a caller reading the draft
   * it had just called `onChange` with would read the value from *before* the
   * add and write the new group away again.
   *
   * Absent on a form, where Save is the commit and this would be a second one.
   */
  onCommit?: (groups: string[]) => void;
  /** Named on the panel, because the options are one sport's and the dictionary
   *  table shows six. */
  sport: string;
  /**
   * Whether the source being configured has been saved yet.
   *
   * **False only on Add Source, and it withdraws the add-a-group control.** A
   * group is made of the labels a source has published, and a source that does
   * not exist has published nothing — so there is nowhere to save a mapping and
   * nothing to map. Offering the dialog there listed every *other* saved source
   * instead, and adding a group would have written a position map onto a
   * profile the operator was not looking at and had not chosen to edit.
   */
  profileExists?: boolean;
  /**
   * The unsaved source, where there is one.
   *
   * Add Source supplies this instead of `profileExists`, and it is what lets a
   * group be made there at all: the labels come from discovery's reading of the
   * payload rather than from a Run, and the mapping goes into the draft the
   * screen will create the profile with.
   */
  draft?: DraftSource;
  compact?: boolean;
  disabled?: boolean;
  /** What a screen reader calls the control. Defaults to the **visible** label
   *  the three screens give it, which is the client's own phrase for the
   *  column — a control announced as something other than its caption is two
   *  names for one thing. */
  label?: string;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  /**
   * Whether the add-a-group dialog is up.
   *
   * Held here rather than by each of the four callers, so every control that
   * offers the groups also offers the way to make one — the reason this
   * component exists at all.
   */
  const [adding, setAdding] = useState(false);

  /**
   * Shut the panel, and tell the caller once.
   *
   * Every route out goes through here — losing focus, Escape from the button,
   * Escape from inside the panel, and clicking the control again — because a
   * caller that saves on close has to be told however it was closed. A route
   * that set `open` directly would be a selection that looked saved and was
   * not.
   *
   * Adding a group does **not** go through here: it shuts the panel to put a
   * dialog up, and what it saves is decided after that dialog returns.
   */
  const shut = () => {
    if (!open) return;
    setOpen(false);
    onCommit?.([...value]);
  };
  // The width floor is `usePlacement`'s own 280, which `Combobox.tsx` records as
  // *measured* against a row holding a label and a check mark — which is this
  // panel's row exactly. Callers there raise that floor when they put something
  // else on every row; nothing here does, and lowering a measured number
  // against no measurement of my own is how a figure stops meaning anything.
  //
  // The height ceiling is lowered, and only that: 320 is sized for a status
  // line over a scrolling list, and fourteen checkboxes with a footer come in
  // under 300 — so the panel is sized to its content instead of opening with
  // a hundred pixels of space under the last row.
  const placement = usePlacement(anchor, open, undefined, 300);

  const caption = summarise(value);
  const empty = value.length === 0;

  return (
    <div
      ref={anchor}
      className="relative min-w-0"
      // Closed by leaving the control, which — because every focusable part of
      // it is inside this div — is exactly "focus went somewhere else".
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) shut();
      }}
    >
      <button
        type="button"
        className={cx(
          "input w-full text-left",
          compact && "input-sm",
          // A real value reads as ink and "No exclusions" as muted, so a
          // glance down the column separates the rows that declare something
          // from the rows that are simply at their default.
          empty && "text-muted-soft",
        )}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? shut() : setOpen(true))}
        // So Escape closes the panel rather than the dialog this may sit in.
        onKeyDown={(event) => {
          if (event.key === "Escape" && open) {
            event.preventDefault();
            event.stopPropagation();
            shut();
          }
        }}
      >
        <span className="block truncate">{caption}</span>
      </button>

      {open && placement && (
        <div
          className={cx(
            "fixed z-50 flex flex-col overflow-hidden rounded-md",
            "border border-hairline bg-canvas shadow-overlay",
          )}
          style={{
            left: placement.left,
            width: placement.width,
            top: placement.top,
            bottom: placement.bottom,
            maxHeight: placement.maxHeight,
          }}
          /*
            **Nothing in here takes focus, so nothing in here closes the panel.**
            The control shuts on focus leaving it, read off `relatedTarget` —
            and a mousedown on anything unfocusable (a group's name, the labels
            under it, the dead space beside them) blurs the trigger with a
            `relatedTarget` of null, which reads as "focus went somewhere else"
            and shuts the panel. Clicking a group's *name* therefore closed the
            menu without ticking it: the panel unmounted before the `<label>`
            could forward the click to its checkbox. Only the checkbox itself
            worked, because it is focusable and so stayed inside the test.

            Preventing the default here keeps focus on the trigger button, so
            the blur never fires. The click is untouched — a label forwards to
            its control on *click*, not on mousedown — so ticking still works,
            Escape still closes, and the scroll container is unaffected.
          */
          onMouseDown={(event) => event.preventDefault()}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              shut();
            }
          }}
        >
          <div className="shrink-0 border-b border-hairline px-sm py-xxs text-caption text-muted-soft">
            {options.length === 0
              ? "Reading this sport's Canonical Position Groups…"
              : `${sport} · Canonical Position Groups`}
          </div>

          {options.length === 0 ? (
            // **The loading state, and not a sport with no groups.** Every sport
            // the endpoint answers for offers at least `UNKNOWN`, which is a
            // real cohort, so the only way this branch is reached is the panel
            // being opened before the query resolves. Saying that beats an
            // empty frame, which reads as a control that failed.
            <div className="px-sm py-sm text-caption text-muted">
              The groups have not arrived yet. Close this and open it again.
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-xs">
              {options.map((group) => (
                // `cursor-pointer` and the box itself, and no hover fill:
                // DESIGN.md §"Don't add hover state styling beyond what the
                // system already encodes", and nothing in `src/` paints a
                // background on hover. The checkbox is the state.
                <label
                  key={group}
                  className="flex cursor-pointer items-start gap-xs px-xs py-xxs text-caption"
                >
                  <Checkbox
                    checked={value.includes(group)}
                    onChange={() => onChange(toggle(options, value, group))}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-ink">{group}</span>
                    {/*
                      Under the name rather than beside it: a group may be fed
                      by ten labels — football's `OL` is — and a row that grew
                      sideways would push the name itself out of a list this
                      narrow. Truncated by the same rule, so the longest group
                      cannot set the width of the menu.
                    */}
                    {(labels?.[group]?.length ?? 0) > 0 && (
                      <span className="truncate text-micro text-muted-soft">
                        {labels![group]!.join(", ")}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          )}

          {/*
            **Always offered, not shown only when the list looks short.** The
            reason somebody needs it is that the position they want is not in
            the list, and a control they have to discover after meeting the dead
            end is a control that arrives too late — the argument `+ New` on the
            Statistic cell already makes.

            It adds a *mapping*, not a name: see `AddPositionGroup.tsx`.

            **Except on Add Source**, where the source does not exist yet: it has
            published no labels to make a group from and has no profile to save
            one on. Said rather than hidden, because the control is present on
            the same dropdown everywhere else and its absence would read as a
            bug rather than as a step not yet reached.
          */}
          {profileExists || draft ? (
            <button
              type="button"
              className="flex shrink-0 items-center gap-xs border-t border-hairline px-sm py-xs text-caption text-muted hover:text-ink"
              onClick={() => {
                shut();
                setAdding(true);
              }}
            >
              <PlusIcon size={14} />
              Add a group…
            </button>
          ) : (
            <span className="shrink-0 border-t border-hairline px-sm py-xs text-micro text-muted-soft">
              A new group is made from the labels a source publishes, so this
              source has to be saved and run before one can be added.
            </span>
          )}

          <div className="flex shrink-0 items-center justify-between gap-xs border-t border-hairline px-sm py-xs">
            <span className="min-w-0 flex-1 truncate text-micro text-muted-soft">
              {empty ? NO_EXCLUSIONS : summarise(value, 4)}
            </span>
            {/* Shown only when it would do something. A disabled Clear beside
                "No exclusions" is a control that teaches the reader to ignore
                the row of controls. */}
            {!empty && (
              <Button
                variant="ghost"
                size="sm"
                icon={<CloseIcon size={14} />}
                onClick={() => onChange([])}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      )}

      {/*
        A group added here is selected immediately. The alternative — adding one
        and then having to find it in a list that has just changed — is the
        shape `NewStatisticForm` already rejected for the Statistic cell.
      */}
      {adding && (profileExists || draft) && (
        <AddPositionGroupModal
          sport={sport}
          draft={draft}
          onClose={() => setAdding(false)}
          onAdded={(group) => {
            setAdding(false);
            // Through `toggle` against the options *plus* the new group, because
            // the query that offers them may not have refetched yet — and a
            // group dropped for not being on offer would be a group the
            // operator just made and cannot see.
            const next = toggle([...options, group], value, group);
            onChange(next);
            // **And saved, explicitly.** The panel shut when this dialog opened,
            // so the ordinary commit has already been and gone; without this the
            // new group would be selected on screen and written nowhere. The
            // value is passed rather than read back, because `onChange` above
            // has not re-rendered the caller yet.
            onCommit?.(next);
          }}
        />
      )}
    </div>
  );
}
